import React from 'react';
import { Tooltip } from 'antd';
import { get, groupBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import * as apis from '@src/api/filters';
import { isInvalidXMLString, operatorMap, type Operator } from '../../utils';
import { type ValidationResult } from 'react-querybuilder';
import * as Types from '../../types';
import {
  InputType,
  type QueryBuilderRule,
  type QueryBuilderGroupRule,
  type QueryBuilderFieldSchema,
  type Query,
} from './types';

export const isEmptyValue = (value) => [null, undefined, ''].includes(value);

export const isTagField = (field?: string) => field === 'TAG';

const validator = (rule: QueryBuilderRule): ValidationResult => {
  const { field, value, key } = rule;
  let valueErrorMsg = '';
  let keyErrorMsg = '';
  // check the key of custom TAG
  if (isTagField(field) && !key) {
    keyErrorMsg = 'The field is required';
  }
  if (!value || !value.length || value.some((it) => [null, undefined, ''].includes(it.value))) {
    valueErrorMsg = 'The field is required';
  } else if (value.some((it) => isInvalidXMLString(it.value))) {
    valueErrorMsg = 'Include invalid character';
  }
  const reasons = [
    valueErrorMsg && {
      type: 'value' as const,
      message: valueErrorMsg,
    },
    keyErrorMsg && {
      type: 'key' as const,
      message: keyErrorMsg,
    },
  ].filter(Boolean);
  return {
    valid: !reasons.length,
    reasons,
  };
};

export const checkError = (validation: ValidationResult | undefined, type: 'value' | 'key') =>
  validation?.reasons?.find((it) => it.type === type);

const getInputType = (dimensionField: Types.DimensionFieldMeta) => {
  const { values, urls, autocompletion_only, value } = dimensionField;
  if (values && values.length) return InputType.Select;

  // Align with the old filter builder.
  if (urls && urls.value) {
    if (!autocompletion_only || isTagField(value)) return InputType.SearchInput;
    if (autocompletion_only) return InputType.SearchSelect;
  }

  return InputType.Input;
};

export const getOperators = () =>
  Object.entries(operatorMap).map(([key, value]) => {
    return {
      name: key,
      label: value,
    };
  });

export const queryValidator = (query?: Query) => {
  if (!query?.rules?.length) return false;

  return query.rules.every((ruleOrGroup) => {
    const groupRules = (ruleOrGroup as QueryBuilderGroupRule).rules;
    if (groupRules) {
      return groupRules.every((it) => validator(it).valid);
    }
    return validator(ruleOrGroup as QueryBuilderRule).valid;
  });
};

export const allowMultiSelection = (op: string) =>
  (['eq', 'contains'] as Operator[]).includes(op as Operator);

export const getDimensionMetaMap = (filterMeta: Types.FilterMeta) => {
  const map = {} as Record<string, Types.DimensionFieldMeta>;
  filterMeta.dimensions.forEach((group) => {
    group.fields.forEach((it) => {
      map[it.value] = it;
    });
  });
  return map;
};

export const getGroupedFieldsByCategory = (fields: QueryBuilderFieldSchema[]) => {
  const grouped = groupBy(fields, 'category');

  return Object.entries(grouped)
    .map(([category, children]) => {
      return {
        originalLabel: category,
        label: category,
        options: children
          .map((it) => ({
            label: <Tooltip title={it.label}>{it.label}</Tooltip>,
            value: it.name,
            originalLabel: it.label,
          }))
          .sort((a, b) => String(a.originalLabel).localeCompare(String(b.originalLabel))),
      };
    })
    .sort((a, b) => String(a.originalLabel).localeCompare(String(b.originalLabel)));
};

export const getFieldSchema = (fieldName: string, fields: QueryBuilderFieldSchema[]) => {
  const fieldSchema = fields.find((it) => it.name === fieldName);
  return fieldSchema;
};

// create an empty rule based on target field meta schema
export const generateRuleByFieldSchema = (
  fieldSchema: QueryBuilderFieldSchema,
): QueryBuilderRule => {
  return {
    field: get(fieldSchema, 'name', ''),
    operator: get(fieldSchema, 'operators[0].value', 'eq'),
    value: [],
    key: null,
  };
};

// create an empty group based on target field meta schema
export const generateGroupByFieldSchema = (
  fieldSchema: QueryBuilderFieldSchema,
  combinator: 'or' | 'and',
): QueryBuilderGroupRule => {
  const rule = generateRuleByFieldSchema(fieldSchema);
  return {
    combinator,
    rules: [rule],
  };
};

// transform filter meta into query builder fileds
export const transformFilterMetaToFields = (
  filterMeta: Types.FilterMeta,
  params: {
    disabledOperators?: string[];
  } = {},
): QueryBuilderFieldSchema[] => {
  if (!filterMeta) return [];

  const { dimensions = [] } = filterMeta;
  const { disabledOperators = [] } = params;
  const fields = [] as QueryBuilderFieldSchema[];

  dimensions.forEach((dimension) => {
    dimension.fields.forEach((it) => {
      const getValueOptions =
        it.urls.value && it.autocompletion_only
          ? async (pattern: string) => {
              const data = await apis
                .getAutoCompletion<{ text: string; value: string }[]>(it.urls.value, pattern)
                .then((res) => res.data);
              return (data || []).map((d) => ({
                label: d.text,
                value: d.value,
              }));
            }
          : undefined;

      const getTagValueOptions =
        isTagField(it.value) && it.urls.value
          ? async (pattern: string, key: string) => {
              const url = `${it.urls.value}?key=${key}`;
              const data = await apis
                .getAutoCompletion<{ text: string; value: string }[]>(url, pattern)
                .then((res) => res.data);
              return (data || []).map((d) => ({
                label: d.text,
                value: d.value,
              }));
            }
          : undefined;

      const getTagKeyOptions =
        isTagField(it.value) && it.urls.key
          ? async (pattern: string) => {
              const data = await apis
                .getAutoCompletion<{ text: string; value: string }[]>(it.urls.key, pattern)
                .then((res) => res.data);
              return (data || []).map((d) => ({
                label: d.text,
                value: d.value,
              }));
            }
          : undefined;

      fields.push({
        category: dimension.name,
        name: it.value,
        label: it.name,
        inputType: getInputType(it),
        operatorOptions: (it.operators || [])
          .filter((o) => !disabledOperators.includes(o.value))
          .map((o) => ({
            value: o.value,
            label: o.name,
          })),
        valueOptions: (it.values || []).map((v) => ({ label: v.name, value: v.value })),
        autoCompletion: Boolean(getValueOptions || getTagValueOptions),
        validator,
        getValueOptions,
        getTagValueOptions,
        getTagKeyOptions,
      });
    });
  });
  return fields.sort((a, b) => a.category.localeCompare(b.category));
};

// transform filter rules into the query
export const transformFilterRulesToQuery = (
  filterRules: Types.Filter['rule'] | undefined,
  fieldsSchema: QueryBuilderFieldSchema[],
  defaultFieldSchema?: QueryBuilderFieldSchema,
): Query => {
  if (!filterRules || !filterRules.length) {
    const initRule = generateRuleByFieldSchema(defaultFieldSchema || fieldsSchema[0]);
    return {
      combinator: 'and',
      rules: [initRule],
    };
  }

  const transferFilterRuleToQueryBuilderRule = (
    filterRule?: Omit<Types.FilterRule, 'value'> & {
      value: {
        label: string;
        value: Types.FilterRule['value'];
      }[];
    },
  ) => {
    // Exclude invalid field to avoid crash
    if (!filterRule) return undefined;

    return {
      field: filterRule.field,
      operator: filterRule.op,
      value: filterRule.value,
      key: filterRule.key ?? null,
    };
  };

  // aggregrate rules for same field with same `eq` and `contains` operator
  const aggregateRulesToSingleRule = (rules: Types.FilterRule[]) => {
    const groupedRules = groupBy(rules, (it) => {
      const field = `${it.field}_${isTagField(it.field) ? it.key : ''}`;
      if (allowMultiSelection(it.op)) {
        return `${field}_${it.op}`;
      } else {
        return `${field}_${it.op}_${it.value}_${uuidv4()}`;
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return Object.entries(groupedRules).map(([_, ruleItems]) => {
      const ruleWithMultiValues = {
        ...ruleItems[0],
        value: [
          {
            label: ruleItems[0].display,
            value: ruleItems[0].value,
          },
        ],
      };
      if (ruleItems.length > 1) {
        for (let i = 1; i < ruleItems.length; i++) {
          ruleWithMultiValues.value.push({
            label: ruleItems[i].display,
            value: ruleItems[i].value,
          });
        }
      }
      return ruleWithMultiValues;
    });
  };

  return {
    combinator: 'and',
    rules: filterRules
      .map((r) => {
        const validR = (r || []).filter(Boolean);
        if (!validR.length) return undefined;

        const aggregateRuleGroups = aggregateRulesToSingleRule(validR);
        if (aggregateRuleGroups.length === 1)
          return transferFilterRuleToQueryBuilderRule(aggregateRuleGroups[0]);
        return {
          combinator: 'or' as const,
          rules: aggregateRuleGroups
            .map((it) => transferFilterRuleToQueryBuilderRule(it))
            .filter(Boolean),
        };
      })
      .filter(Boolean) as any,
  };
};

// convert query into filter rule
export const convertQueryToFilterRules = (query?: Query): Types.Filter['rule'] => {
  if (!query) return [];

  const transferQueryBuilderRuleToFilterRule = (builderRule: QueryBuilderRule) => {
    if (Array.isArray(builderRule.value)) {
      return builderRule.value.map((v) => {
        return {
          field: builderRule.field,
          op: builderRule.operator,
          value: String(v.value ?? ''),
          key: builderRule.key ?? null,
          display: v.label ?? '',
        };
      });
    }
    return [
      {
        field: builderRule.field,
        op: builderRule.operator,
        value: String(builderRule.value ?? ''),
        key: builderRule.key ?? null,
        display: builderRule.display ?? builderRule.value,
      },
    ];
  };

  const rules = query.rules
    .map((it) => {
      if ((it as QueryBuilderGroupRule).rules) {
        const groupRules = [] as Types.FilterRule[];
        (it as QueryBuilderGroupRule).rules.forEach((r) => {
          const data = transferQueryBuilderRuleToFilterRule(r);
          groupRules.push(...data);
        });
        return groupRules;
      } else {
        return transferQueryBuilderRuleToFilterRule(it as QueryBuilderRule);
      }
    })
    .filter((it) => !!it.length);
  return rules;
};
